import { Company } from 'module/api/company/types';
import { EmailPage } from 'api/pages/types';
import { useGetEmailPagesFromPagesQuery } from 'queries/pages';
import { useMemo } from 'react';

/**
 * Transforms flat Page[] from scarlet /v1/pages to EmailPage[] shape for existing UI.
 * Revert to useGetEmailPagesForCompanyQuery (get-pages-grouped) to use grouped API again.
 */
function pagesToEmailPages(
  pages: {
    documentId: number;
    title: string;
    sourceId: number;
    updatedAt?: string;
  }[],
): EmailPage[] {
  return pages.map((p) => ({
    title: p.title,
    count: 1,
    associations: [
      {
        title: p.title,
        documentId: p.documentId,
        templateId: 0,
        sourceId: p.sourceId,
        updatedAt: p.updatedAt,
      },
    ],
  }));
}

interface Props {
  company: Company;
  searchText: string;
}
interface Result {
  emailPages: EmailPage[];
  allEmailPages: EmailPage[];
  isEmailPagesLoading: boolean;
}
export const useEmailPagesList = ({ company, searchText }: Props): Result => {
  // Scarlet API: GET /v1/pages?pageType=EMAIL&companyId=... (same as meeting pages)
  const { data: pagesFromApi, isLoading } = useGetEmailPagesFromPagesQuery(
    company.companyId,
  );

  // Legacy: get-pages-grouped (keep for revert)
  // const { data: emailDocuments, isLoading } = useGetEmailPagesForCompanyQuery(
  //   company.companyId,
  // );

  const emailDocuments = useMemo(
    () => (pagesFromApi ? pagesToEmailPages(pagesFromApi) : undefined),
    [pagesFromApi],
  );

  // If we have data (even if it's an empty array), we're not loading anymore
  const isEmailPagesLoading = isLoading && emailDocuments === undefined;

  const filteredMeetingDocuments = useMemo(() => {
    return emailDocuments?.filter((email) => {
      const searchLower = searchText.toLowerCase();

      const mainTitleMatch = email.title?.toLowerCase().includes(searchLower);

      const associationTitleMatch = email?.associations?.some((association) =>
        association.title?.toLowerCase().includes(searchLower),
      );

      return mainTitleMatch || associationTitleMatch;
    });
  }, [emailDocuments, searchText]);

  return {
    emailPages: filteredMeetingDocuments ?? [],
    allEmailPages: emailDocuments ?? [],
    isEmailPagesLoading,
  };
};
